/**
 * System Status Service
 * Handles system status updates and persistence
 */

import config from '../config/environment';

const STATUS_STORAGE_KEY = 'belvoir_system_status';

/**
 * Get current system status from localStorage and/or API
 * @returns {Promise<Object>} - Current system status
 */
export const getSystemStatus = async () => {
  try {
    // Try to get from localStorage first (faster)
    const cached = localStorage.getItem(STATUS_STORAGE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      // If cache is recent (less than 5 minutes old), use it
      if (parsed.lastUpdated && (Date.now() - new Date(parsed.lastUpdated).getTime() < 5 * 60 * 1000)) {
        return parsed;
      }
    }

    // Fetch from API
    const apiUrl = `${config.apiEndpoint}/custom/v1/system-status`;
    const response = await fetch(apiUrl);
    
    if (response.ok) {
      const data = await response.json();
      // Cache it
      localStorage.setItem(STATUS_STORAGE_KEY, JSON.stringify({
        ...data,
        lastUpdated: new Date().toISOString()
      }));
      return data;
    } else {
      // Fallback to default status if API fails
      return getDefaultStatus();
    }
  } catch (error) {
    console.error('[SystemStatus] Error fetching status:', error);
    // Return cached or default status
    const cached = localStorage.getItem(STATUS_STORAGE_KEY);
    return cached ? JSON.parse(cached) : getDefaultStatus();
  }
};

/**
 * Send email notification when system status changes
 * @param {string} systemId - System ID ('booking', 'website', etc.)
 * @param {Object} updates - The status updates applied
 * @param {Object} previousStatus - The previous status (optional)
 * @returns {Promise<boolean>} - Success status
 */
const sendStatusChangeEmail = async (systemId, updates, previousStatus = null) => {
  const systemNames = {
    booking: 'Booking System',
    website: 'Website',
    datacentre: 'Data Centre',
    membership: 'Membership System'
  };

  const systemName = systemNames[systemId] || systemId;
  
  try {
    const statusEmoji = updates.status === 'operational' ? '✅' : 
                       updates.status === 'degraded' ? '⚠️' : 
                       updates.status === 'outage' ? '🔴' : '🔧';
    
    const statusText = updates.statusText || (
      updates.status === 'operational' ? 'OPERATIONAL' :
      updates.status === 'degraded' ? 'DEGRADED' :
      updates.status === 'outage' ? 'OUTAGE' : 'UNKNOWN'
    );

    const subject = `${statusEmoji} System Status Changed: ${systemName} - ${statusText}`;
    
    const emailBody = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.6; color: #333; background: #f5f5f5; margin: 0; padding: 0;">
  <div style="max-width: 600px; margin: 0 auto; padding: 20px; background: #ffffff;">
    
    <!-- Header -->
    <div style="background-color: #101d40; background: linear-gradient(135deg, #101d40 0%, #2c3e50 100%); color: #ffffff !important; padding: 30px 25px; border-radius: 12px; margin-bottom: 25px;">
      <div style="font-size: 36px; margin-bottom: 10px; color: #ffffff !important;">${statusEmoji}</div>
      <h1 style="margin: 0; font-size: 24px; font-weight: 700; color: #ffffff !important;">System Status Changed</h1>
      <p style="margin: 10px 0 0 0; opacity: 0.9; font-size: 16px; color: #ffffff !important;">${systemName}</p>
    </div>

    <!-- Status Change Info -->
    <div style="background: ${updates.status === 'operational' ? '#d4edda' : updates.status === 'degraded' ? '#fff3cd' : '#f8d7da'}; border-left: 4px solid ${updates.status === 'operational' ? '#28a745' : updates.status === 'degraded' ? '#ffc107' : '#dc3545'}; padding: 20px; margin-bottom: 25px; border-radius: 8px;">
      <h2 style="margin: 0 0 10px 0; font-size: 18px; color: ${updates.status === 'operational' ? '#155724' : updates.status === 'degraded' ? '#856404' : '#721c24'};">
        New Status: ${statusText}
      </h2>
      ${previousStatus ? `<p style="margin: 0; color: #666; font-size: 14px;">Previous: ${previousStatus.status?.toUpperCase() || 'UNKNOWN'}</p>` : ''}
    </div>

    <!-- Description -->
    ${updates.description ? `
    <div style="background: #f8f9fa; padding: 20px; border-radius: 8px; margin-bottom: 25px;">
      <h3 style="margin: 0 0 10px 0; font-size: 16px; color: #101d40;">Description</h3>
      <p style="margin: 0; color: #666; line-height: 1.6;">${updates.description}</p>
    </div>
    ` : ''}

    <!-- Incident Details -->
    ${updates.incident ? `
    <div style="background: #fff; border: 2px solid #e9ecef; padding: 20px; border-radius: 8px; margin-bottom: 25px;">
      <h3 style="margin: 0 0 15px 0; font-size: 16px; color: #101d40;">📋 Incident Details</h3>
      <p style="margin: 0 0 10px 0;"><strong>Title:</strong> ${updates.incident.title || 'N/A'}</p>
      <p style="margin: 0 0 10px 0;"><strong>Description:</strong> ${updates.incident.description || 'N/A'}</p>
      <p style="margin: 0;"><strong>Detected:</strong> ${updates.incident.timestamp || new Date().toLocaleString('en-GB')}</p>
      
      ${updates.incident.updates && updates.incident.updates.length > 0 ? `
        <div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #e9ecef;">
          <p style="margin: 0 0 10px 0; font-weight: 600; color: #101d40;">Updates:</p>
          ${updates.incident.updates.map(update => `
            <div style="background: #f8f9fa; padding: 10px; border-radius: 4px; margin-bottom: 8px;">
              <p style="margin: 0 0 5px 0; color: #666; font-size: 14px;">${update.description}</p>
              <p style="margin: 0; color: #999; font-size: 12px; font-style: italic;">${update.timestamp}</p>
            </div>
          `).join('')}
        </div>
      ` : ''}
    </div>
    ` : ''}

    <!-- Timestamp -->
    <div style="background: #f8f9fa; padding: 15px; border-radius: 8px; margin-bottom: 25px;">
      <p style="margin: 0; color: #666; font-size: 14px;">
        <strong>Changed at:</strong> ${new Date().toLocaleString('en-GB', {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        })}
      </p>
    </div>

    <!-- Quick Actions -->
    <div style="background: #f8f9fa; padding: 20px; border-radius: 8px; margin-bottom: 25px;">
      <h3 style="margin: 0 0 15px 0; font-size: 16px; color: #101d40;">🔗 Quick Links</h3>
      <p style="margin: 0 0 10px 0;">
        <a href="${window.location.origin}/status" style="color: #007bff; text-decoration: none; font-weight: 600;">View Public Status Page →</a>
      </p>
      <p style="margin: 0 0 10px 0;">
        <a href="${window.location.origin}/security-center" style="color: #007bff; text-decoration: none; font-weight: 600;">Open Security Center →</a>
      </p>
      <p style="margin: 0;">
        <a href="${window.location.origin}/security-center#monitoring" style="color: #007bff; text-decoration: none; font-weight: 600;">Error Monitoring Dashboard →</a>
      </p>
    </div>

    <!-- Footer -->
    <div style="text-align: center; padding-top: 20px; border-top: 1px solid #e9ecef; color: #666; font-size: 14px;">
      <p style="margin: 0;">Belvoir Castle - System Status Notification</p>
      <p style="margin: 5px 0 0 0; font-size: 12px; opacity: 0.7;">This is an automated notification from the booking system</p>
    </div>

  </div>
</body>
</html>`;

    // Try the new system-status-notification endpoint first
    let apiUrl = `${config.apiEndpoint}/custom/v1/send-system-status-notification`;
    let response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to: 'jhartwell@belvoircastle.com',
        subject: subject,
        body: emailBody,
        systemId,
        systemName,
        status: updates.status,
        statusText,
        timestamp: new Date().toISOString()
      })
    });

    // If the new endpoint doesn't exist (404), fallback to error notification endpoint
    if (!response.ok && response.status === 404) {
      console.warn(`⚠️ [SystemStatus] New endpoint not found, using fallback endpoint`);
      
      // Use the existing error notification endpoint as fallback
      apiUrl = `${config.apiEndpoint}/custom/v1/send-error-notification`;
      response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: 'jhartwell@belvoircastle.com',
          subject: subject,
          body: emailBody,
          errorDetails: {
            errorType: 'SYSTEM_STATUS_CHANGED',
            endpoint: 'System Status',
            orderId: systemId,
            bookingReference: systemId,
            timestamp: new Date().toISOString(),
            systemName,
            status: updates.status,
            statusText
          }
        })
      });
    }

    if (response.ok) {
      const result = await response.json();
      console.log(`✅ [SystemStatus] Status change email sent for ${systemName}`, result);
      return true;
    } else {
      const errorText = await response.text();
      console.error(`❌ [SystemStatus] Failed to send status change email:`, {
        status: response.status,
        statusText: response.statusText,
        error: errorText,
        endpoint: apiUrl
      });
      throw new Error(`Failed to send email: ${response.status} ${response.statusText}`);
    }
  } catch (error) {
    console.error(`❌ [SystemStatus] Error sending status change email:`, {
      error: error.message,
      stack: error.stack,
      systemId,
      systemName
    });
    throw error; // Re-throw so caller can handle it
  }
};

/**
 * Update system status
 * @param {string} systemId - System ID to update ('booking', 'website', etc.)
 * @param {Object} updates - Updates to apply
 * @returns {Promise<boolean>} - Success status
 */
export const updateSystemStatus = async (systemId, updates) => {
  try {
    console.log(`[SystemStatus] Updating ${systemId} status:`, updates);

    // Get current status before updating (for email comparison)
    const currentStatus = await getSystemStatus();
    const systemIndex = currentStatus.systems.findIndex(s => s.id === systemId);
    const previousStatus = systemIndex !== -1 ? { ...currentStatus.systems[systemIndex] } : null;
    
    // Update localStorage immediately for fast UI response
    if (systemIndex !== -1) {
      currentStatus.systems[systemIndex] = {
        ...currentStatus.systems[systemIndex],
        ...updates
      };
      currentStatus.lastUpdated = new Date().toISOString();
      localStorage.setItem(STATUS_STORAGE_KEY, JSON.stringify(currentStatus));
    }

    // Send email notification about status change (don't await - run async)
    // Catch errors but log them clearly
    sendStatusChangeEmail(systemId, updates, previousStatus).catch(err => {
      console.error('❌ [SystemStatus] Failed to send status change email:', {
        error: err.message,
        systemId,
        systemName: systemId === 'booking' ? 'Booking System' : 
                   systemId === 'website' ? 'Website' :
                   systemId === 'datacentre' ? 'Data Centre' :
                   systemId === 'membership' ? 'Membership System' : systemId,
        details: 'Check console for full error details. WordPress endpoint may not be implemented yet.'
      });
      // Don't throw - email failure shouldn't block status update
    });

    // Send update to API
    const apiUrl = `${config.apiEndpoint}/custom/v1/system-status`;
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        systemId,
        updates
      })
    });

    if (response.ok) {
      console.log(`[SystemStatus] Successfully updated ${systemId} status`);
      return true;
    } else {
      console.error(`[SystemStatus] Failed to update ${systemId} status:`, response.status);
      return false;
    }
  } catch (error) {
    console.error(`[SystemStatus] Error updating ${systemId} status:`, error);
    return false;
  }
};

/**
 * Mark booking system as deprecated
 * @param {Object} context - Error context
 * @returns {Promise<boolean>} - Success status
 */
export const markBookingSystemDeprecated = async (context = {}) => {
  const incident = {
    title: 'Booking System Fallback Required',
    description: `The booking system encountered an issue processing ${context.pageName || 'an event'} booking. ${context.errorMessage || 'Required booking information could not be retrieved.'}`,
    timestamp: new Date().toLocaleString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }),
    updates: context.updates || []
  };

  return await updateSystemStatus('booking', {
    status: 'degraded',
    statusText: 'DEPRECATED',
    description: 'Booking system is experiencing issues. Please contact support for assistance.',
    incident
  });
};

/**
 * Get default system status
 * @returns {Object} - Default status
 */
const getDefaultStatus = () => ({
  systems: [
    {
      id: 'website',
      name: 'Website',
      icon: '🌐',
      status: 'operational',
      description: 'Main website and public pages are fully operational.'
    },
    {
      id: 'booking',
      name: 'Booking System',
      icon: '🎫',
      status: 'operational',
      description: 'Online ticket booking and event reservations are working normally.'
    },
    {
      id: 'datacentre',
      name: 'Data Centre',
      icon: '🔒',
      status: 'operational',
      description: 'All servers and infrastructure are running smoothly.'
    },
    {
      id: 'membership',
      name: 'Membership System',
      icon: '👥',
      status: 'operational',
      description: 'Membership renewals and upgrades are working normally.'
    }
  ],
  lastUpdated: new Date().toISOString()
});

export default {
  getSystemStatus,
  updateSystemStatus,
  markBookingSystemDeprecated
};

